import React, { useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useAppDispatch } from '../../../../app/hooks';
import { tryToFetchAllFeedbacks } from '../../../../store/admin/feedbacks/feedbacksSlice';
import FeedbacksTable from '../tables/FeedbacksTable';
import Button from '../../../../shared/button';
import Modal from '../../../../shared/modal';
import Error from '../../../../shared/error';
import { rome } from '../../../../utils/date';

interface IExportModal {
    openModal: boolean;
    data?: any;
    totalCount?: number;
    handleCloseModal: () => void;
    onGettingFilters: () => void;
}

const ExportFeedbacksModal = ({
    openModal,
    data,
    totalCount,
    handleCloseModal,
    onGettingFilters
}: IExportModal) => {
    const dispatch = useAppDispatch();
    const csvLink = React.useRef() as React.MutableRefObject<any>;
    const [loadingDownloadCSV, setLoadingDownloadCSV] = useState<boolean>(false)
    const [loadingDownloadExcel, setLoadingDownloadExcel] = useState<boolean>(false)
    const [downloadCSV, setDownloadCSV] = useState<any>()
    const [submitError, setSubmitError] = useState<any>();

    const onCloseModal = () => {
        handleCloseModal();
    };

    const formatDateToRome = (value: any) => {
        if (value === null || value === undefined || value === '') return value;
        if (typeof value === 'number') {
            const parsedNumber = rome(value);
            if (!parsedNumber.isValid()) return value;
            return parsedNumber.format('DD MMM YYYY [at] h:mm a');
        }
        if (typeof value !== 'string') return value;

        const trimmed = value.trim();
        // Only normalize machine-friendly date formats; leave human-formatted
        // strings untouched to avoid day/month misinterpretation.
        const isIsoLike = /^\d{4}-\d{2}-\d{2}(?:[T ][\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(trimmed);
        if (!isIsoLike) return value;

        const parsed = rome(trimmed);
        if (!parsed.isValid()) return value;
        return parsed.format('DD MMM YYYY [at] h:mm a');
    };

    const normalizeExportDataToRomeTimezone = (exportData: any) => {
        if (!Array.isArray(exportData) || exportData.length === 0) return exportData;
        const headers = exportData[0];
        if (!Array.isArray(headers)) return exportData;

        const dateColumnIndexes = headers.reduce((acc: number[], header: any, index: number) => {
            if (typeof header === 'string' && /(date|time|created)/i.test(header)) {
                acc.push(index);
            }
            return acc;
        }, []);

        if (dateColumnIndexes.length === 0) return exportData;

        return exportData.map((row: any, rowIndex: number) => {
            if (rowIndex === 0 || !Array.isArray(row)) return row;
            return row.map((cell: any, cellIndex: number) =>
                dateColumnIndexes.includes(cellIndex) ? formatDateToRome(cell) : cell,
            );
        });
    };

    const exportToExcel = (data: string[][], fileName: string = 'data.xlsx') => {
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        saveAs(blob, fileName);
        setLoadingDownloadExcel(false)
    };

    const onDownloadData = async (type: string) => {
        if (type === 'csv') setLoadingDownloadCSV(true)
        if (type === 'excel') setLoadingDownloadExcel(true)
        try {
            setSubmitError(undefined)
            const filters: any = onGettingFilters()
           
            const formatBody = {
                data: filters,
            }
            const response: any = await dispatch(tryToFetchAllFeedbacks(formatBody)).unwrap()
            const normalizedResponse = normalizeExportDataToRomeTimezone(response)
            if (type === 'excel') {
                exportToExcel(normalizedResponse, 'Feedbacks-data.xlsx')
            } else {
                setDownloadCSV(normalizedResponse || [])
            }
        } catch (err) {
            setSubmitError(`${err}`)
            setLoadingDownloadCSV(false)
            setLoadingDownloadExcel(false)
        }
    }

    useEffect(() => {
        if (downloadCSV && (loadingDownloadCSV)) {
            csvLink.current.link.click()
        }
        setLoadingDownloadCSV(false)
        setLoadingDownloadExcel(false)
    }, [downloadCSV])

    return (
        <Modal
            open={openModal}
            title='Export Data - Feedbacks'
            headerStyle='border-b-1 py-3 mb-4'
            onClose={onCloseModal}
            contentContainerStyle='!rounded-xl min-w-[95%] md:min-w-[70%] lg:min-w-[60%]'>
            <div className='p-2 max-w-[95vw] md:min-w-[70%] lg:min-w-[60%] min-h-[500px]'>
                <div className='flex flex-col md:flex-row md:justify-between my-3'>
                    <p className='text-primary-light dark:text-primary-darkMode mb-3'>How do you want your data exported?</p>
                    <div className='flex flex-col md:flex-row'>
                        <Button
                            label={loadingDownloadExcel ? 'Preparing data' : 'Download as .xlxs'}
                            className='btn-primary-rounded-less dark:!bg-black md:mr-3 mb-4 md:mb-0'
                            onClickButton={() => !loadingDownloadExcel && onDownloadData('excel')}
                        />
                        {/* @ts-ignore */}
                        <CSVLink
                            ref={csvLink}
                            data={downloadCSV?.filter((item: any, idx: number, arr: any) => { return idx > 0 }) || []}
                            headers={downloadCSV?.[0] || []}
                            className={'none'}
                            target={'_blank'}
                            filename={`Feedbacks-data.csv`}
                        ></CSVLink>
                        <Button
                            label={loadingDownloadCSV ? 'Preparing data' : 'Download as .csv'}
                            className='btn-primary-rounded-less dark:!bg-black md:mr-3'
                            onClickButton={() => !loadingDownloadCSV && onDownloadData('csv')}
                        />
                    </div>
                </div>
                {submitError && <Error text={submitError} />}
                <FeedbacksTable
                    rows={data}
                    totalCount={totalCount ?? data?.length ?? 0}
                />
            </div>
        </Modal>
    );
};

export default ExportFeedbacksModal;